import React, { useEffect, useState, useRef } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, TextField, IconButton } from '@mui/material';

interface EditableReferenceNumbersInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
}

interface ReferenceNumbersValue {
  number1: string;
  number2: string;
  number3: string;
}

export const EditableReferenceNumbersInput: React.FC<
  EditableReferenceNumbersInputProps
> = ({ value, onChange, onSave, onCancel }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const parseInitialValue = (val: any): ReferenceNumbersValue => {
    if (typeof val === 'string') {
      const trimmedValue = val.trim();
      const parts = trimmedValue.split(/[/,]/).map((p) => p.trim());

      let number1 = '';
      let number2 = '';
      let number3 = '';

      if (parts[0]) {
        number1 = parts[0] && parts[0] !== '-' ? parts[0] : '';
      }

      if (parts[1]) {
        number2 = parts[1] && parts[1] !== '-' ? parts[1] : '';
      }

      if (parts[2]) {
        number3 = parts[2] && parts[2] !== '-' ? parts[2] : '';
      }

      return { number1, number2, number3 };
    }
    return { number1: '', number2: '', number3: '' };
  };

  const [localValue, setLocalValue] = useState<ReferenceNumbersValue>(() =>
    parseInitialValue(value),
  );

  useEffect(() => {
    setLocalValue(parseInitialValue(value));
  }, [value]);

  const formatValueForSave = (
    referenceValue: ReferenceNumbersValue,
  ): string => {
    const num1 = referenceValue.number1 || '-';
    const num2 = referenceValue.number2 || '-';
    const num3 = referenceValue.number3 || '-';

    return `${num1}/${num2},${num3}`;
  };

  const handleNumberChange = (
    field: keyof ReferenceNumbersValue,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newValue = event.target.value;
    const updatedValue = { ...localValue, [field]: newValue };
    setLocalValue(updatedValue);
    onChange(formatValueForSave(updatedValue));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Enter') {
      onSave(formatValueForSave(localValue));
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const currentFocus = document.activeElement;

      if (
        !containerRef.current ||
        !containerRef.current.contains(currentFocus)
      ) {
        onSave(formatValueForSave(localValue));
      }
    }, 100);
  };

  const handleClear = () => {
    const clearedValue = { number1: '', number2: '', number3: '' };
    setLocalValue(clearedValue);
    onChange(formatValueForSave(clearedValue));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onSave(formatValueForSave(localValue));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [localValue, onSave]);

  return (
    <Box
      ref={containerRef}
      display='flex'
      gap={0.5}
      alignItems='center'
      onKeyDown={handleKeyDown}
      sx={{
        width: '100%',
        height: 'auto',
        position: 'relative',
        paddingRight: '18px',
        paddingLeft: '2px',
        gap: '2px',
      }}
      data-reference-numbers-container
    >
      {(localValue.number1 || localValue.number2 || localValue.number3) && (
        <IconButton
          size='small'
          onClick={handleClear}
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            padding: '1px',
            zIndex: 10,
          }}
        >
          <ClearIcon sx={{ fontSize: '12px' }} />
        </IconButton>
      )}
      <TextField
        size='small'
        value={localValue.number1}
        onChange={(e) => handleNumberChange('number1', e)}
        onBlur={handleBlur}
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          margin: 0,
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            border: 'none !important',
            borderBottom: 'none !important',
          },
          '& .MuiInputBase-input': {
            padding: '0px 4px',
            lineHeight: '1.5em',
            height: '24px',
            fontSize: '0.7rem',
          },
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.7rem',
          color: 'text.secondary',
          flexShrink: 0,
        }}
      >
        /
      </Box>

      <TextField
        size='small'
        value={localValue.number2}
        onChange={(e) => handleNumberChange('number2', e)}
        onBlur={handleBlur}
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          margin: 0,
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            borderBottom: 'none !important',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          },
          '& .MuiInputBase-input': {
            padding: '0px 4px',
            lineHeight: '1.5em',
            height: '24px',
            fontSize: '0.7rem',
          },
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.7rem',
          color: 'text.secondary',
          flexShrink: 0,
        }}
      >
        ,
      </Box>

      <TextField
        size='small'
        value={localValue.number3}
        onChange={(e) => handleNumberChange('number3', e)}
        onBlur={handleBlur}
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          margin: 0,
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            border: 'none !important',
            borderBottom: 'none !important',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          },
          '& .MuiInputBase-input': {
            padding: '0px 4px',
            lineHeight: '1.5em',
            height: '24px',
            fontSize: '0.7rem',
          },
        }}
      />
    </Box>
  );
};
